import { useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Breadcrumb, SkipLink } from '@/shared/ui/primitives';
import { Icon } from '@/shared/ui/icon';
import { BreadcrumbProvider } from '@/shared/ui/layout/BreadcrumbProvider';
import { useBreadcrumbItems } from '@/shared/ui/layout/useBreadcrumb';
import { WorkerizeMark, WorkerizeMarkThemed } from '@/shared/ui/brand';
import { useCredits } from '@/features/billing/credits/hooks/useCredits';
import { useTheme } from '@/app/providers/useTheme';
import { NotificationsBell } from '@/features/notifications/components/NotificationsBell';
import { ForcePasswordChangeGate } from '@/features/identity-management/auth/components/ForcePasswordChangeGate';
import { useT } from '@/shared/i18n';
import { resolvePageTitleKey } from './app-nav';
import { AppLocaleToggle } from './AppLocaleToggle';
import { AppMobileDrawer } from './AppMobileDrawer';
import { AppSidebar } from './AppSidebar';
import { AppThemeToggle } from './AppThemeToggle';
import { AppUserMenu } from './AppUserMenu';
import { TopbarSearch } from './TopbarSearch';

/**
 * Authenticated app shell for the internal Workerize workspace.
 *
 * Desktop chrome is a fixed 68px Ink icon rail + a top bar; the rail and top
 * bar stay put while the routed Outlet owns its own scroll regions (the
 * dashboard's bento column + insight rail each scroll independently). Future
 * product modules mount inside the Outlet; this file owns only shell chrome,
 * navigation, and user controls.
 *
 * Wrapped in a `BreadcrumbProvider` so the routed screen can publish its
 * breadcrumb trail (via `WorkspaceScreen` / `useSetBreadcrumb`) up to the top
 * bar, which renders it as the page heading.
 */
export function AppLayout(): ReactNode {
  return (
    <BreadcrumbProvider>
      <AppShell />
    </BreadcrumbProvider>
  );
}

function AppShell(): ReactNode {
  const { t } = useT();
  const { credits } = useCredits();
  const { theme } = useTheme();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const breadcrumbItems = useBreadcrumbItems();

  // Credit icon: brand primary (Ink) on light for contrast, Lime on dark.
  const creditIconClass =
    theme === 'dark' ? 'text-secondary-fixed' : 'text-[var(--text-primary)]';

  // The top-bar breadcrumb is the page heading. Fall back to the route-derived
  // title for screens that don't publish their own trail.
  const titleKey = resolvePageTitleKey(location.pathname);
  const pageTitle = titleKey ? t(titleKey) : t('app.name');
  const topbarItems =
    breadcrumbItems.length > 0 ? breadcrumbItems : [{ label: pageTitle }];

  return (
    <div
      data-testid="app-shell"
      className="flex h-[100dvh] overflow-hidden bg-[var(--surface-base)] text-[var(--text-primary)]"
    >
      <SkipLink />
      <ForcePasswordChangeGate />
      <AppMobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      {/* Desktop icon rail — permanent, 68px wide. */}
      <aside
        data-testid="app-sidebar"
        className="relative z-20 hidden w-[4.25rem] shrink-0 flex-col items-center border-e border-[var(--ink-band-border)] bg-[var(--ink-band)] py-5 md:flex"
      >
        <Link
          to="/app"
          aria-label={t('app.name')}
          className="mb-8 flex h-10 w-10 items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60"
        >
          <WorkerizeMark size={30} />
        </Link>
        <div className="flex w-full flex-1 flex-col px-3">
          <AppSidebar collapsed />
        </div>
        <div className="mt-6 flex w-full justify-center px-3">
          <AppUserMenu compact />
        </div>
      </aside>

      {/* Right column: top bar + routed content. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header
          data-testid="app-topbar"
          className="relative z-30 flex h-16 shrink-0 items-center gap-4 border-b border-[var(--border-subtle)] bg-[var(--surface-base)]/80 px-4 backdrop-blur sm:px-8"
        >
          <button
            type="button"
            aria-label={t('appShell.drawer.open')}
            onClick={() => setDrawerOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-[var(--text-primary)] transition-colors hover:bg-[var(--surface-container)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60 md:hidden"
          >
            <Icon name="menu" size={18} />
          </button>

          {/* Mobile logo — the new themed mark doubles as the home affordance
              (no left rail on mobile). */}
          <Link
            to="/app"
            aria-label={t('app.name')}
            className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60 md:hidden"
          >
            <WorkerizeMarkThemed size={26} />
          </Link>

          {/* Mobile: page heading — breadcrumb without the home icon (the mark
              above is the workspace home). */}
          <div className="flex min-w-0 flex-1 items-center overflow-hidden md:hidden">
            <Breadcrumb label={t('breadcrumb.label')} items={topbarItems} asPageHeading />
          </div>

          {/* Desktop: breadcrumb trail with its leading home icon. */}
          <div className="hidden min-w-0 flex-1 items-center md:flex">
            <Breadcrumb
              label={t('breadcrumb.label')}
              homeTo="/app"
              homeLabel={t('breadcrumb.home')}
              items={topbarItems}
              asPageHeading
            />
          </div>

          {/* End: quick-search → credits → locale → theme → notifications. */}
          <div className="ms-auto flex items-center gap-3">
            <div className="hidden w-56 lg:block">
              <TopbarSearch />
            </div>
            {credits ? (
              <span className="hidden h-10 items-center gap-1.5 text-sm font-semibold text-[var(--text-primary)] sm:inline-flex">
                <Icon name="coins" size={18} className={creditIconClass} />
                <span className="sr-only">
                  {credits.unlimited
                    ? t('appShell.credits.unlimited')
                    : `${t('appShell.credits.label')}: `}
                </span>
                {credits.unlimited ? '∞' : credits.balance}
              </span>
            ) : null}
            <AppLocaleToggle />
            <AppThemeToggle />
            <NotificationsBell />
          </div>
        </header>

        <main
          id="main-content"
          data-testid="main-content"
          tabIndex={-1}
          className="min-h-0 flex-1 overflow-hidden focus-visible:outline-none"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
